/**
 * SearchView (P67.2, #486).
 *
 * Dedicated /search route. Reads `?q=` from the URL, hits the
 * backend, renders results grouped by kind. Each row clicks
 * through to the entity's `surface_url` returned by the backend
 * (Twin detail page or Foundation admin drawer-deep-link).
 */
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { findTerms } from "shared/glossary/terms";

import { HighlightedText } from "shared/search/HighlightedText";
import { useSearch, type SearchHit } from "shared/search/useSearch";

import { Arrow } from "shared/components/Arrow";
// Order matches backend SEARCHABLE_KINDS so kind-sections render in
// the same order across surfaces.
const KIND_ORDER: readonly string[] = [
  "ibs_service",
  // Round 17 L6 -- functions headed as what they are, not as IBSes.
  "internal_function",
  "service",
  "capability",
  "vendor",
  "product",
  "configuration_item",
  "channel",
  "journey",
  "risk_appetite_statement",
  // P69: live-data surfaces appended below the catalogue.
  "finding",
  "evidence_artefact",
  // E7: the framework surfaces. A control identifier is the most
  // natural thing anyone types into this box.
  "framework_control",
  "cap",
  "regulator_letter",
  // Round 20 H58 -- signatories and account holders, by name.
  "person",
];

const KIND_LABELS: Record<string, string> = {
  framework_control: "Controls",
  cap: "Corrective action plans",
  regulator_letter: "Regulator letters",
  person: "People",
  ibs_service: "IBSes",
  internal_function: "Critical internal functions",
  service: "Services",
  capability: "Capabilities",
  vendor: "Vendors",
  product: "Products",
  configuration_item: "Configuration items",
  channel: "Channels",
  journey: "Journeys",
  risk_appetite_statement: "Risk appetite statements",
  finding: "Findings",
  evidence_artefact: "Evidence artefacts",
};

function groupByKind(hits: SearchHit[]): Record<string, SearchHit[]> {
  const out: Record<string, SearchHit[]> = {};
  for (const hit of hits) {
    (out[hit.kind] ||= []).push(hit);
  }
  return out;
}

/**
 * One result row. Renders as a react-router <Link> for internal
 * surfaces or as an <a target=_blank> for external URIs
 * (SharePoint / Confluence artefacts). The "<Arrow kind="up" />" glyph signals
 * the new-tab behaviour to keyboard + visual users.
 */
function SearchResultRow({ hit, query }: { hit: SearchHit; query: string }) {
  const rowContent = (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-slate-900">
          <HighlightedText text={hit.display_name} query={query} />
          {hit.external && (
            <span
              data-testid="search-hit-external-glyph"
              aria-label="opens in new tab"
              className="ms-1 text-slate-400"
            >
              <Arrow kind="up" />
            </span>
          )}
        </span>
        {/* H6: the relevance score was on show. It is our ranking
            internals -- a reader cannot act on "0.42", and the order
            of the list already says what it says. */}
      </div>
      {hit.snippet && (
        <p className="mt-0.5 text-xs text-slate-600 line-clamp-2">
          <HighlightedText text={hit.snippet} query={query} />
        </p>
      )}
    </>
  );
  const baseClass = "block px-4 py-3 hover:bg-slate-50";
  const testId = `search-hit-${hit.kind}-${hit.id}`;
  if (hit.external) {
    return (
      <a
        href={hit.surface_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        data-external="true"
        className={baseClass}
      >
        {rowContent}
      </a>
    );
  }
  return (
    <Link
      to={hit.surface_url}
      data-testid={testId}
      data-external="false"
      className={baseClass}
    >
      {rowContent}
    </Link>
  );
}

export function SearchView() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const glossaryHits = findTerms(q);
  const { data, isLoading, isError } = useSearch({ q });

  const grouped = useMemo(() => (data ? groupByKind(data.results) : {}), [data]);
  const orderedKinds = useMemo(
    () => KIND_ORDER.filter((k) => grouped[k]?.length),
    [grouped],
  );

  return (
    <article className="space-y-6" data-testid="search-view">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("home.search.viewHeading")}
        </h1>
        {q && (
          <p className="mt-1 text-sm text-slate-600">
            {t("home.search.searchedFor", { query: q })}
          </p>
        )}
      </header>

      {/* F1 -- searching "IBS" used to return every service with those
          letters in its name and never the definition, which is what
          somebody typing an acronym into a search box is usually
          after. The definition leads; the estate follows. */}
      {q && glossaryHits.length > 0 && (
        <section data-testid="search-glossary" className="space-y-2">
          <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
            {t("glossary.searchHeading")}
          </h2>
          {glossaryHits.slice(0, 2).map(({ key, term }) => (
            <Link
              key={key}
              to={`/glossary#${key}`}
              data-testid={`search-glossary-${key}`}
              className="block rounded border border-paper-2 bg-white px-3 py-2 hover:border-brand-brass"
            >
              <span className="block text-sm font-medium text-slate-900">
                {term.term}
              </span>
              <span className="block text-xs text-slate-600">{term.short}</span>
            </Link>
          ))}
        </section>
      )}

      {!q && (
        <p data-testid="search-empty-query" className="text-sm text-slate-500">
          {t("home.search.emptyQuery")}
        </p>
      )}

      {q && isLoading && (
        <p data-testid="search-loading" className="text-sm text-slate-500">
          {t("home.search.loading")}
        </p>
      )}

      {q && isError && (
        <p data-testid="search-error" className="text-sm text-rose-700">
          {t("home.search.error")}
        </p>
      )}

      {q && data && data.results.length === 0 && !isLoading && (
        <p data-testid="search-no-results" className="text-sm text-slate-500">
          {t("home.search.noResults")}
        </p>
      )}

      {data && (data.withheld_modules?.length ?? 0) > 0 && (
        <p
          data-testid="search-withheld"
          className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {t("home.search.withheld", {
            modules: (data.withheld_modules ?? []).join(", "),
          })}
        </p>
      )}

      {data && data.truncated && (
        <p data-testid="search-truncated" className="text-xs italic text-amber-700">
          {t("home.search.truncated")}
        </p>
      )}

      {orderedKinds.map((kind) => (
        <section
          key={kind}
          data-testid={`search-section-${kind}`}
          className="space-y-2"
        >
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {KIND_LABELS[kind] ?? kind}
          </h2>
          <ul className="divide-y divide-slate-200 border border-slate-200 rounded-md bg-white">
            {(grouped[kind] ?? []).map((hit) => (
              <li key={hit.id}>
                <SearchResultRow hit={hit} query={q} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </article>
  );
}
